import type { ReactNode } from 'react';
import { useState } from 'react';
import { ConfirmationModal } from '@components/ConfirmationModal';
import { useEndorsementSummaryMutations } from '@ContractBuilder/hooks/useEndorsementSummaryMutations';
import { useSectionMutations } from '@ContractBuilder/hooks/useSectionMutations';
import { useAttachmentMutations } from '@features/attachments/hooks/useAttachmentMutations';
import { useLanguage } from '@src/language';
import { isEndorsementViewPath } from '@utils/app-paths';

import { useEntityStore } from '../../store';

type Entry = 'attachment' | 'block' | 'section' | 'clause';

interface ConfirmDeleteModalProps {
  handleClose: () => void;
  callback?: () => void;
  title: string;
  id: string;
  helperText?: ReactNode;
  type: Entry;
}

const ConfirmDeleteModal = ({ id, title, handleClose, helperText, type, callback }: ConfirmDeleteModalProps) => {
  const isEndorsementView = isEndorsementViewPath();
  const [isLoading, setIsLoading] = useState(false);
  const { deleteBlock } = useEntityStore(({ deleteBlock }) => ({ deleteBlock }));
  const { deleteSection } = useSectionMutations();
  const { deleteSummaryBlock, deleteSummarySection } = useEndorsementSummaryMutations();
  const { deleteAttachment } = useAttachmentMutations();

  const { getContent } = useLanguage({ prefix: 'naming.mrc' });

  const handleDelete = async () => {
    const isBlockDelete = type === 'block' || type === 'clause';
    setIsLoading(true);
    try {
      if (type === 'attachment') {
        await deleteAttachment.mutateAsync(id);
      }

      if (isBlockDelete && isEndorsementView) {
        await deleteSummaryBlock.mutateAsync({ blockId: id });
      } else if (isBlockDelete) {
        await deleteBlock(id);
      }

      const isSectionDelete = type === getContent('section').toLowerCase();

      if (isSectionDelete && isEndorsementView) {
        await deleteSummarySection.mutateAsync({ sectionId: id });
      } else if (isSectionDelete) {
        await deleteSection.mutateAsync(id);
      }
    } finally {
      setIsLoading(false);
      handleClose();
      callback?.();
    }
  };

  return (
    <ConfirmationModal
      data-testid="confirm-delete-modal"
      title={`Remove this ${type}?`}
      description={
        <>
          Are you sure you would like to remove the {type}
          {title && <span className="font-bold text-gray-800">: {title}</span>}
        </>
      }
      confirmLabel={`Remove ${type}`}
      confirmDataTestId="confirm-delete-button"
      isLoading={isLoading}
      onClose={handleClose}
      onConfirm={handleDelete}
    >
      {helperText && <div className="mt-4 text-sm italic">{helperText}</div>}
    </ConfirmationModal>
  );
};

export default ConfirmDeleteModal;
